import { getPreference } from '@atlassian/jira-business-preferences/src/services/view-preferences/index.tsx';
import type { ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import type { SortingAttributes } from '@atlassian/jira-business-common/src/common/types/sorting.tsx';

import { sortDirections } from '../../common/constants.tsx';

const DEFAULT_LIST_VIEW_ORDER_FIELD = 'rank';
const DEFAULT_LIST_VIEW_SORT_DIRECTION = sortDirections.ASC;

export const getDefaultListViewJqlWithFilter = (projectKey: ProjectKey, filter: string) =>
	`project = "${projectKey}" AND ${filter} ORDER BY ${DEFAULT_LIST_VIEW_ORDER_FIELD} ${DEFAULT_LIST_VIEW_SORT_DIRECTION}`;

export const getDefaultListViewJql = (projectKey: ProjectKey) =>
	`project = "${projectKey}" ORDER BY ${DEFAULT_LIST_VIEW_ORDER_FIELD} ${DEFAULT_LIST_VIEW_SORT_DIRECTION}`;

export const getDefaultListViewJqlWithSorting = (
	projectKey: ProjectKey,
	sorting: SortingAttributes,
) =>
	`project = "${projectKey}" ORDER BY ${sorting.sortBy} ${sorting.direction}, ${DEFAULT_LIST_VIEW_ORDER_FIELD} ${sorting.direction}`;

export const getDefaultListViewJqlWithFilterAndSorting = (
	projectKey: ProjectKey,
	filter: string,
	sorting: SortingAttributes,
) =>
	`project = "${projectKey}" AND ${filter} ORDER BY ${sorting.sortBy} ${sorting.direction}, ${DEFAULT_LIST_VIEW_ORDER_FIELD} ${sorting.direction}`;

export type MigrationData = {
	projectId: string;
	groupBy: string | null;
	filter: string | null;
	sorting: SortingAttributes | null;
};

const parsePreferenceValue = (value: unknown): string | null => {
	if (typeof value === 'string') return value;
	if (value === null || value === undefined) return null;
	try {
		return JSON.stringify(value);
	} catch {
		return String(value);
	}
};

const parseSortingPreferenceValue = (value: SortingAttributes): SortingAttributes | null => {
	if (typeof value !== 'object' || value === null) {
		return null;
	}
	return value ?? null;
};

export const getMigrationDataForProject = (projectId: string): MigrationData => {
	try {
		const groupByValue = getPreference(projectId, 'list', 'groupBy');
		const filterValue = getPreference(projectId, 'list', 'filter');
		const sortingValue = getPreference(projectId, 'list', 'sorting');
		return {
			projectId,
			groupBy: parsePreferenceValue(groupByValue),
			filter: parsePreferenceValue(filterValue),
			sorting: parseSortingPreferenceValue(sortingValue),
		};
	} catch (error) {
		return {
			projectId,
			groupBy: null,
			filter: null,
			sorting: null,
		};
	}
};

export const hasMigrationDataForProject = (projectId: string): boolean => {
	const migrationData = getMigrationDataForProject(projectId);
	return (
		migrationData.groupBy !== null ||
		migrationData.filter !== null ||
		migrationData.sorting !== null
	);
};

export const getJqlFromMigrationData = (migrationData: MigrationData, projectKey: string) => {
	const { filter, sorting } = migrationData;

	if (filter && sorting) {
		return getDefaultListViewJqlWithFilterAndSorting(projectKey, filter, sorting);
	}

	if (filter) {
		return getDefaultListViewJqlWithFilter(projectKey, filter);
	}

	if (sorting) {
		return getDefaultListViewJqlWithSorting(projectKey, sorting);
	}

	return getDefaultListViewJql(projectKey);
};
